import gsap from 'gsap'
import {Color, Euler, Group, Matrix4, Mesh, ShaderMaterial, Vector2, Vector3, Vector4} from 'three'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js'

import InvertocatGLB from '../../../assets/images/brandedDivider/invertocat.glb'
import {getRenderer} from '../../_commonObjects'
import {bgColor, canvasResSize} from '../../_config'
import {BREAKPOINT} from '../../_rawConfig'
import fragmentShader from '../../_shaders/cube/cube'
import invertocatFrag from '../../_shaders/invertocat'
import vertexShader from '../../_shaders/vertexShader'
import type {CubeConfig} from './_types'
import type RoundedCubeGeometry from './RoundedCubeGeometry'

interface InvertocatCubeProps {
  cubeConfig: CubeConfig
  cubeGeometry: RoundedCubeGeometry
  rectData: Vector4
  _rectData: Vector4
}

export default class InvertocatCube {
  group: Group = new Group()
  invertocatMesh: Mesh | null = null
  cubeGeometry: RoundedCubeGeometry
  cubeConfig: CubeConfig
  progress: Vector4 = new Vector4(0, 0, 0, 0)
  commonUniforms: {
    uInnerShadowColor: {value: Color}
    uBgColor: {value: Color}
    uPixelRatio: {value: number}
    uLightPos: {value: Vector3}
    uRectData: {value: Vector4}
    uRectData2: {value: Vector4}
    uResolution: {value: Vector2}
  } = {
    uInnerShadowColor: {value: new Color(0x5fed83)},
    uBgColor: {value: bgColor},
    uPixelRatio: {value: 1},
    uLightPos: {value: new Vector3(2.0, 2.0, 1.0).normalize()},
    uRectData: {value: new Vector4()},
    uRectData2: {value: new Vector4()},
    uResolution: {value: canvasResSize},
  }
  isLoaded: boolean = false
  isNeededToPlayTl: boolean = false
  invertocatParams: {rotation: Vector3; position: Vector3}
  tl: gsap.core.Timeline = gsap.timeline({paused: true})
  lightPos: Vector3 = new Vector3(1.0, 1.0, 1.0).normalize()
  constructor(props: InvertocatCubeProps, invertocatParams: {rotation: Vector3; position: Vector3}) {
    this.cubeGeometry = props.cubeGeometry
    this.cubeConfig = props.cubeConfig
    this.invertocatParams = invertocatParams
    // this.group.visible = false;
    this.group.name = 'invertocatCube'

    this.commonUniforms.uRectData.value = props.rectData
    this.commonUniforms.uRectData2.value = props._rectData
  }

  async create(group: Group) {
    group.add(this.group)

    const loader = new GLTFLoader()
    const mesh = this.createCubeMesh()

    this.group.userData.position = {
      def: this.cubeConfig.position.clone(),
      offset: this.cubeConfig.offsetStart.position.clone(),
      current: new Vector3(0, 0, 0),
    }

    this.group.userData.progress = this.progress

    const scale = this.cubeConfig.scale

    if (scale instanceof Vector3) {
      this.group.scale.copy(scale)
    } else {
      this.group.scale.set(scale, scale, scale)
    }

    this.group.userData.rotation = {
      def: this.cubeConfig.rotation.clone(),
      offset: this.cubeConfig.offsetStart.rotation.clone(),
      current: new Vector3(0, 0, 0),
      defMatrix: new Matrix4(),
      offsetMatrix: new Matrix4(),
      currentMatrix: new Matrix4(),
      defEuler: new Euler(),
      offsetEuler: new Euler(),
    }

    this.group.add(mesh)
    await loader.loadAsync(InvertocatGLB).then(glsl => {
      glsl.scene.traverse(child => {
        if (child instanceof Mesh) {
          // const helper = new AxesHelper(5);
          // this.group.add(helper);
          this.invertocatMesh = child
          this.invertocatMesh.material = new ShaderMaterial({
            vertexShader,
            fragmentShader: invertocatFrag,
            uniforms: {
              ...this.commonUniforms,
              uColor1: {value: new Color(0x04a732)}, // Dark Green
              uColor2: {value: new Color(0x5fed83)}, // Light Green
              uLightPos: {value: this.lightPos},
            },
          })

          this.invertocatMesh.scale.set(0.2, 0.2, 0.2)

          this.invertocatMesh.position.set(
            this.invertocatParams.position.x * 0.87,
            this.invertocatParams.position.y * 0.87,
            this.invertocatParams.position.z * 0.87,
          )
          this.invertocatMesh.rotation.set(
            this.invertocatParams.rotation.x,
            this.invertocatParams.rotation.y,
            this.invertocatParams.rotation.z,
          )

          this.group.add(this.invertocatMesh)
        }
      })

      this.createAnimation()
      this.isLoaded = true
    })
  }

  createCubeMesh() {
    const progress = new Vector4(1, 0, 0, 0)

    let material: ShaderMaterial
    const random = new Vector4(Math.random(), Math.random(), Math.random(), Math.random())

    material = new ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        ...this.commonUniforms,
        uColor1: {value: new Color(0x028e29)}, // Dark Green
        uColor2: {value: new Color(0xbfffd1)}, // Light Green
        uColor3: {value: new Color(0x21a545)}, // Light Green2

        uProgress: {value: progress},
        uRandom: {value: random},
      },
    })

    const cube = new Mesh(this.cubeGeometry, material)
    cube.userData.progress = progress

    return cube
  }

  createAnimation() {
    const delay = this.cubeConfig.delay
    const duration = this.cubeConfig.duration

    this.tl
      .add(() => {
        this.group.visible = true
      }, delay)
      .to(
        this.group.userData.position.offset,
        {
          x: 0,
          y: 0,
          z: 0,
          ease: 'expo.out',
          duration,
        },
        delay,
      )
      .to(
        this.group.userData.rotation.offset,
        {
          x: 0,
          y: 0,
          z: 0,
          ease: 'expo.out',
          duration,
        },
        delay,
      )
      .to(
        this.group.userData.progress,
        {
          x: 1,
          ease: 'expo.out',
          duration: 0.8,
        },
        delay,
      )
      .to(
        this.group.userData.progress,
        {
          y: 1,
          duration: 1.5,
        },
        delay,
      )
      .to(
        this.group.userData.progress,
        {
          z: 1,
          ease: 'steps(8)',
          duration: 1.8,
        },
        delay,
      )

    if (this.invertocatMesh) {
      this.tl.to(
        this.invertocatMesh.position,
        {
          x: this.invertocatParams.position.x,
          y: this.invertocatParams.position.y,
          z: this.invertocatParams.position.z,
          ease: 'expo.out',
          duration: 3,
        },
        delay + 0.5,
      )
    }

    if (this.isNeededToPlayTl) {
      this.tl.play()
    }
  }

  playTl() {
    this.isNeededToPlayTl = true
    if (this.isLoaded) {
      this.tl.play()
    }
  }

  update() {
    const renderer = getRenderer()
    if (!renderer) return
    if (this.group.userData.position === undefined || this.group.userData.rotation === undefined) return
    this.commonUniforms.uPixelRatio.value = renderer.getPixelRatio()
    const position = this.group.userData.position
    const rotation = this.group.userData.rotation

    position.current.copy(position.def).add(position.offset)
    this.group.position.copy(position.current)

    const layout = this.cubeConfig.layout

    if (window.innerWidth < BREAKPOINT.MOBILE) {
      this.group.visible = layout.mobile.visible
      this.group.position.add(layout.mobile.offset)
    } else if (window.innerWidth < BREAKPOINT.TABLET) {
      this.group.visible = layout.tablet.visible
      this.group.position.add(layout.tablet.offset)
    }

    // Apply rotations using matrix composition: rotation.def * rotation.offset
    rotation.defEuler.set(rotation.def.x, rotation.def.y, rotation.def.z, 'XYZ')
    rotation.offsetEuler.set(rotation.offset.x, rotation.offset.y, rotation.offset.z, 'XYZ')
    rotation.defMatrix.makeRotationFromEuler(rotation.defEuler)
    rotation.offsetMatrix.makeRotationFromEuler(rotation.offsetEuler)

    // Compose rotations: first apply def rotation, then offset rotation
    rotation.currentMatrix.multiplyMatrices(rotation.offsetMatrix, rotation.defMatrix)

    // Extract the final rotation back to Euler and apply to mesh
    this.group.rotation.setFromRotationMatrix(rotation.currentMatrix)
  }
}
